import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, AlertTriangle, RefreshCw, CheckCircle2 } from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fee-mismatch audit page — surfaces every registration or ad-hoc booking
// where the client-sent total_amount differed from the server-recomputed
// value by more than $0.01. This is the visible counterpart to the
// [FEE MISMATCH] log line — admins should never have to tail logs.
export default function AdminFeeMismatches() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ parent_reg: 0, adhoc: 0, term_drift: 0 });
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/academy/admin/fee-mismatches`, { withCredentials: true });
      setRows(r.data?.rows || []);
      setCounts({
        parent_reg: r.data?.parent_registration_count || 0,
        adhoc: r.data?.adhoc_booking_count || 0,
        term_drift: r.data?.term_enrolment_drift_count || 0,
      });
    } catch (err) {
      console.error("Failed to load fee mismatches", err);
      toast.error(err.response?.data?.detail || "Failed to load fee mismatches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const formatMoney = (v) => (v == null ? "-" : `$${Number(v).toFixed(2)}`);
  const formatDate = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 sm:p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white" data-testid="fee-mismatches-back">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </Link>

        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-red-400" />
              Fee Mismatches
            </h1>
            <p className="text-slate-400 text-sm mt-1 max-w-2xl">
              Registrations and ad-hoc bookings where the client-sent total differed from the server-recomputed value. Server-computed is authoritative — the delta shows what a tampered or stale client would have charged.
            </p>
          </div>
          <Button onClick={load} variant="outline" data-testid="fee-mismatches-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card
            onClick={() => setSourceFilter("all")}
            className={`p-4 bg-slate-900 border cursor-pointer transition-colors ${sourceFilter === "all" ? "border-white" : "border-slate-700 hover:border-slate-500"}`}
            data-testid="fee-mismatches-filter-all"
          >
            <p className="text-xs text-slate-400 uppercase tracking-wide">Total flagged</p>
            <p className="text-3xl font-bold" data-testid="fee-mismatches-total">{rows.length}</p>
          </Card>
          <Card
            onClick={() => setSourceFilter("parent_registration")}
            className={`p-4 bg-slate-900 border cursor-pointer transition-colors ${sourceFilter === "parent_registration" ? "border-fuchsia-400" : "border-slate-700 hover:border-slate-500"}`}
            data-testid="fee-mismatches-filter-registration"
          >
            <p className="text-xs text-slate-400 uppercase tracking-wide">Parent registrations</p>
            <p className="text-3xl font-bold text-fuchsia-400">{counts.parent_reg}</p>
          </Card>
          <Card
            onClick={() => setSourceFilter("adhoc_booking")}
            className={`p-4 bg-slate-900 border cursor-pointer transition-colors ${sourceFilter === "adhoc_booking" ? "border-orange-400" : "border-slate-700 hover:border-slate-500"}`}
            data-testid="fee-mismatches-filter-adhoc"
          >
            <p className="text-xs text-slate-400 uppercase tracking-wide">Ad-hoc bookings</p>
            <p className="text-3xl font-bold text-orange-400">{counts.adhoc}</p>
          </Card>
          <Card
            onClick={() => setSourceFilter("term_enrolment_drift")}
            className={`p-4 bg-slate-900 border cursor-pointer transition-colors ${sourceFilter === "term_enrolment_drift" ? "border-red-400" : "border-slate-700 hover:border-slate-500"}`}
            data-testid="fee-mismatches-filter-term-drift"
          >
            <p className="text-xs text-slate-400 uppercase tracking-wide">Term drift (ledger)</p>
            <p className="text-3xl font-bold text-red-400">{counts.term_drift}</p>
          </Card>
        </div>

        {loading ? (
          <Card className="p-8 bg-slate-900 border-slate-700 text-center text-slate-400">
            Loading…
          </Card>
        ) : rows.length === 0 ? (
          <Card className="p-8 bg-emerald-500/5 border border-emerald-500/30 text-center" data-testid="fee-mismatches-empty">
            <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
            <p className="font-medium text-emerald-200">No fee mismatches</p>
            <p className="text-sm text-emerald-300/80 mt-1">All recent totals match the server-computed values.</p>
          </Card>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-800">
            <table className="w-full text-sm" data-testid="fee-mismatches-table">
              <thead className="bg-slate-900 text-slate-400 uppercase text-xs tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Parent / Player</th>
                  <th className="text-left px-3 py-2">Context</th>
                  <th className="text-right px-3 py-2">Client sent</th>
                  <th className="text-right px-3 py-2">Server computed</th>
                  <th className="text-right px-3 py-2">Delta</th>
                  <th className="text-left px-3 py-2">Notes</th>
                  <th className="text-left px-3 py-2">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {rows.filter(r => sourceFilter === "all" || r.source === sourceFilter).map((r) => {
                  const sourceLabel = r.source === "adhoc_booking" ? "Ad-hoc"
                    : r.source === "term_enrolment_drift" ? "Term drift"
                    : "Registration";
                  const sourceClass = r.source === "adhoc_booking"
                    ? "bg-orange-500/15 text-orange-300 border border-orange-500/30"
                    : r.source === "term_enrolment_drift"
                      ? "bg-red-500/15 text-red-300 border border-red-500/30"
                      : "bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/30";
                  return (
                  <tr key={`${r.source}-${r.id}`} className="bg-slate-950 hover:bg-slate-900/70" data-testid={`fee-mismatch-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase ${sourceClass}`}>
                        {sourceLabel}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.parent_email || "—"}</div>
                      {r.player_name && <div className="text-xs text-slate-400">{r.player_name}</div>}
                      {r.parent_name && !r.player_name && <div className="text-xs text-slate-400">{r.parent_name}</div>}
                    </td>
                    <td className="px-3 py-2">
                      {r.session_name && <div>{r.session_name}</div>}
                      {r.term_name && <div className="text-xs text-slate-400">{r.term_name}</div>}
                      {r.booking_reference && <div className="text-[10px] text-slate-500 font-mono">{r.booking_reference}</div>}
                      {r.booking_type && <div className="text-[10px] text-slate-500">{r.booking_type}</div>}
                      {r.payment_status && <div className="text-[10px] text-slate-500">status: {r.payment_status}</div>}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-300" title={r.source === "term_enrolment_drift" ? "Ledger amount" : "Client sent"}>
                      {formatMoney(r.client_total_amount)}
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-emerald-300" title={r.source === "term_enrolment_drift" ? "fee_breakdown.total" : "Server computed"}>
                      {formatMoney(r.server_total_amount)}
                    </td>
                    <td className={`px-3 py-2 text-right font-mono font-medium ${
                      (r.delta || 0) > 0 ? "text-emerald-400" : (r.delta || 0) < 0 ? "text-red-400" : "text-slate-400"
                    }`}>
                      {(r.delta || 0) > 0 ? "+" : ""}{formatMoney(r.delta)}
                    </td>
                    <td className="px-3 py-2">
                      {r.tier_downgrade_applied && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase bg-amber-500/15 text-amber-300 border border-amber-500/30">
                          cross-tier
                        </span>
                      )}
                      {r.drift_kind === "breakdown_inconsistent" && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase bg-red-500/15 text-red-300 border border-red-500/30" title="Sum of line_items disagrees with fee_breakdown.total">
                          breakdown inconsistent
                        </span>
                      )}
                      {r.drift_kind === "amount_disagrees_with_breakdown" && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase bg-amber-500/15 text-amber-300 border border-amber-500/30" title="Ledger amount differs from fee_breakdown.total">
                          amount ≠ breakdown
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-400">{formatDate(r.created_at)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <Card className="p-4 bg-amber-500/5 border border-amber-500/30 text-sm text-amber-200/80">
          <p className="font-medium text-amber-200 mb-1">How to read this</p>
          <ul className="text-xs leading-snug space-y-1 list-disc pl-4">
            <li><strong>Registration / Ad-hoc</strong>: client-sent vs server-computed total. Delta &gt; 0 → server charged more than the client tried to send (tamper or stale calc). Server value is authoritative; the row is safe.</li>
            <li><strong>Term drift (ledger)</strong>: <code>payment_transactions.amount</code> vs <code>fee_breakdown.total</code>. Positive delta → the ledger charged the parent more than the breakdown says. Negative → less. <span className="text-red-300">These need reconciling</span> — either amend the ledger, refund the parent, or bill the difference.</li>
            <li><strong>breakdown inconsistent</strong> tag means the sum of line_items itself does not match the breakdown&apos;s stored total — always a bug worth fixing in whichever endpoint wrote that row.</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
